import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquarePlus, Pencil, Trash2, Check, X } from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson, apiDelete } from '../../lib/api.js'
import { asList, callTs } from '../../lib/datetime.js'
import { bkkDayISO, bkkTodayISO } from '../../lib/newcase.js'
import { Section } from '../ui/DrawerPrimitives.jsx'
import {
  Button, ConfirmDialog, EmptyState, IconButton, InlineError, Input, Loading, fmtDate, fmtTime, useToast,
} from '../../kit'
import { codedNote, splitNote, EXCLUDE_WARNING, OUTCOME_GROUPS, CANCEL_STATUS } from '../../data/implantOutcomes.js'
import { useMe } from '../../lib/me.js'
import { isAdmin } from '../../lib/role.js'
import RecallNoteText, { OutcomePill, OutcomeIcon } from './RecallNoteText.jsx'

// The patient's recall call-back log with add / edit / delete, author-gated.
// Extracted verbatim from RecallPopup.jsx (its only inputs are patientId / meId /
// onChanged, and it closes over none of that drawer's lock or realtime state) so
// the ติดตาม patient page shows the same list with the same rules rather than a
// second implementation that will drift.
//
// `quickCodes` (optional) turns the note box into a coded one. It is what makes
// the รากเทียม campaign measurable: /reports/implant passes the 12 outcome codes
// from src/data/implantOutcomes.js. Tapping one SELECTS that outcome (it lights
// up; tap again to unselect); the box holds only the caller's own words; and on
// save codedNote() stores `[IMP|C|PR] words` so the note can be parsed back later.
// `recall` (table 47) has no campaign or outcome column, so a machine-readable
// prefix is the only way to report per-list results without a schema change.
//
// 👁 Nobody reads that prefix any more. Until 2026-09-17 the chip wrote it INTO
// the box and every list printed it raw: `[IMP|A|OC]`. Staff could type over it (a
// damaged exclusion code stops excluding, silently), and a second tap wiped what they had
// typed. Every note in this list — coded or not, from any page — now renders
// through RecallNoteText, and editing a coded note edits only its words.
//
// 🔑 It has to be ONE TAP. P9 — the share of recall rows that do not start
// `[IMP|`, target ≤5% — runs at ~40% if the operator has to type an 11-character
// prefix ninety times a week, and then none of the campaign is measurable.
//
// 📞 A call outcome with no call record today is odd (Ram, 2026-10-29). Saving a chip
// first reads this patient's calls for today (Bangkok) — any direction, any result, a
// rang-out ไม่รับสาย counts — fresh from /api/phone, never off the drawer's list. None
// → a ConfirmDialog that says so, and the user may still save (บันทึกต่อ). The record
// usually lands ~20 s after hang-up (90% within 1 min, 99% within ~2 min, measured
// 2026-10-29) and 2-4% of outbound calls match no patient (number on file differs),
// so the dialog says both. A failed lookup counts as "called": a broken read must not
// nag. Plain notes (no chip) are never checked.
//
// ⛔ ยกเลิกเคส is the one chip that does more than write a note. Saving it opens a
// ConfirmDialog, then sets patient.status = ยกเลิกเคส (the clinic's one do-not-recall
// switch — every recall list obeys it) and only then stores the note, so a note can
// never claim a cancel that did not happen. Undo is the status, on the patient page;
// deleting the note restores nothing, and the dialog says so.
//
// Every other caller passes neither prop and is completely unchanged: the chips
// row does not render, and the box behaves exactly as it does today on
// /follow-ups/recall and the patient page.
// `readOnly` (the implant รีวิว tab, 2026-10-30): the history shows, nothing can be
// added, edited or deleted. A reviewer's plain note here used to bump the patient's
// last_recall as if someone had phoned; reviewers write in "บันทึกของผู้รีวิว" instead.
export default function RecallNotesSection({
  patientId, meId, onChanged, refreshKey = 0,
  notePrefix = '', quickCodes = [], readOnly = false,
}) {
  const [rows, setRows] = useState(null)   // null = loading
  const [err, setErr] = useState('')
  const [adding, setAdding] = useState('')
  const [code, setCode] = useState(null)   // selected outcome chip (coded box only)
  // Deleting is author-only, EXCEPT for an admin. A เคสไม่เหมาะ / ไปทำที่อื่นแล้ว note
  // parks a patient off every implant list until a new round and anyone may write one, so a
  // wrong one by someone who is away (or has left) must still be undoable. The proxy
  // (functions/api/recall/[id].js) has always allowed admins; the button did not.
  // Editing stays author-only.
  const canModerate = isAdmin(useMe())
  const addRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [editId, setEditId] = useState(null)
  const [editText, setEditText] = useState('')
  const [confirmDel, setConfirmDel] = useState(null)
  const [confirmCancel, setConfirmCancel] = useState(false)   // ยกเลิกเคส waits on the dialog
  const [confirmNoCall, setConfirmNoCall] = useState(false)   // a chip, but no call today
  const [callMissing, setCallMissing] = useState(false)       // said inside the ยกเลิกเคส dialog too
  const [authors, setAuthors] = useState({})   // employee_id → record (for the code)
  const toast = useToast()

  const load = useCallback(() => {
    if (patientId == null) return
    setErr('')
    apiGet('/api/recall/history', { patient_id: patientId })
      .then((r) => setRows(asList(r).sort((a, b) => recallTs(b) - recallTs(a))))
      .catch((e) => { setRows([]); setErr(String(e.message || e)) })
  }, [patientId])
  useEffect(() => { setRows(null); load() }, [patientId, load])
  // A coded box starts empty on every patient, never carrying the last person's
  // half-typed note — or their picked outcome — into the next call.
  useEffect(() => { setCode(null); if (quickCodes.length) setAdding('') }, [patientId, quickCodes.length])
  const picked = code ? quickCodes.find((c) => c.code === code) || null : null
  const clearDraft = () => { setAdding(''); setCode(null) }
  // A realtime recall event (another station added or edited a note) bumps
  // refreshKey. Reloads WITHOUT clearing, so an open editor is not yanked away.
  useEffect(() => { if (refreshKey) load() }, [refreshKey, load])

  // Notes store the author as an id (created_by/creator) — resolve each to its
  // employee record so we can show the code. cachedGet dedupes across notes/popups.
  useEffect(() => {
    if (!Array.isArray(rows) || !rows.length) return
    const ids = [...new Set(rows.map(noteAuthorId).filter((v) => v != null))]
    let alive = true
    Promise.all(ids.map((id) => cachedGet(`/api/employees/${id}`).then((e) => [id, e]).catch(() => [id, null])))
      .then((pairs) => { if (alive) setAuthors(Object.fromEntries(pairs)) })
    return () => { alive = false }
  }, [rows])
  const authorCode = (r) => {
    const obj = (r.created_by && typeof r.created_by === 'object') ? r.created_by
      : (r.creator && typeof r.creator === 'object') ? r.creator : null
    if (obj?.code) return obj.code
    const id = noteAuthorId(r)
    if (id == null) return ''
    const e = authors[id]
    return e?.code || e?.nickname || e?.name_eng || `#${id}`
  }

  // Any call with this patient today (Bangkok)? Unknown counts as yes — see the top.
  async function calledToday() {
    const today = bkkTodayISO()
    try {
      const r = await apiGet('/api/phone', { patient_id: patientId, start_date: today, end_date: today, per_page: 100 })
      return asList(r).some((c) => bkkDayISO(callTs(c)) === today)
    } catch { return true }
  }
  // picked.seed carries the list letter of the page that passed the chips.
  const draftNote = () => (picked ? codedNote(splitNote(picked.seed).list, picked.code, adding) : adding.trim())

  async function addNote() {
    const Note = draftNote()
    if (!Note || busy) return
    let missing = false
    if (picked) {
      setBusy(true)
      missing = !(await calledToday())
      setBusy(false)
    }
    if (picked?.cancels) { setCallMissing(missing); setConfirmCancel(true); return }
    if (missing) { setConfirmNoCall(true); return }
    await saveNote(Note)
  }
  async function saveNote(Note) {
    setBusy(true)
    try {
      await postJson('/api/recall', { patient_id: patientId, Note })
      clearDraft()
      setErr('')
      toast.success('เพิ่มโน้ตแล้ว')
      load(); onChanged?.()
    } catch (e) { setErr(String(e.message || e)); toast.error('เพิ่มโน้ตไม่สำเร็จ') } finally { setBusy(false) }
  }
  // ยกเลิกเคส: the status first, the note second. A failed status writes nothing; a
  // failed note after it says plainly that the patient IS cancelled.
  async function cancelCase() {
    const Note = draftNote()
    setBusy(true)
    try {
      await postJson(`/api/patients/status/${patientId}`, { status: CANCEL_STATUS })
    } catch (e) {
      setConfirmCancel(false); setBusy(false)
      setErr(`เปลี่ยนสถานะไม่สำเร็จ — ยังไม่ได้ยกเลิกเคส (${String(e.message || e)})`)
      toast.error('ยกเลิกเคสไม่สำเร็จ')
      return
    }
    try {
      await postJson('/api/recall', { patient_id: patientId, Note })
      clearDraft()
      setErr('')
      toast.success('ยกเลิกเคสแล้ว')
    } catch (e) {
      setErr(`เปลี่ยนสถานะเป็นยกเลิกเคสแล้ว แต่บันทึกโน้ตไม่สำเร็จ — กดเพิ่มโน้ตอีกครั้ง (${String(e.message || e)})`)
      toast.error('บันทึกโน้ตไม่สำเร็จ')
    } finally {
      setConfirmCancel(false); setBusy(false)
      load(); onChanged?.()
    }
  }
  async function saveEdit(r) {
    // A coded note keeps its outcome; only the words are editable. Changing the
    // outcome is delete + add, so an edit can never turn a note into (or out of) an
    // implant exclusion (เคสไม่เหมาะ / ไปทำที่อื่นแล้ว).
    const n = splitNote(r.Note)
    const Note = n.code ? codedNote(n.list, n.code, editText) : editText.trim()
    if (!Note) return
    setBusy(true)
    try {
      await patchJson(`/api/recall/${r.id}`, { Note, patient_id: patientId })
      setEditId(null); setEditText('')
      setErr('')
      toast.success('บันทึกแล้ว')
      load()
    } catch (e) { setErr(String(e.message || e)); toast.error('บันทึกไม่สำเร็จ') } finally { setBusy(false) }
  }
  async function del(r) {
    setBusy(true)
    try {
      await apiDelete(`/api/recall/${r.id}?patient_id=${encodeURIComponent(patientId)}`)
      setConfirmDel(null)
      setErr('')
      toast.success('ลบโน้ตแล้ว')
      load(); onChanged?.()
    } catch (e) { setConfirmDel(null); setErr(String(e.message || e)); toast.error('ลบโน้ตไม่สำเร็จ') } finally { setBusy(false) }
  }

  return (
    <Section title="โน้ต Recall" icon={MessageSquarePlus} count={rows?.length}>
      {/* Campaign outcomes — one tap selects, the box keeps what she typed, and the
          prefix is attached on save. Only rendered when a caller supplies them. */}
      {quickCodes.length > 0 && !readOnly && (
        <div className="mb-2">
          <div className="flex flex-wrap gap-1">
            {quickCodes.map((c) => {
              const on = code === c.code
              return (
                // 🔑 Picking a chip hands focus to the box. A clicked button keeps
                // focus, so without this the Enter meant to SAVE re-clicks the chip
                // and unselects it — and the form then looks exactly like a
                // successful save. For เคสไม่เหมาะ (no words needed) that is a
                // silently lost exclusion. mousedown is prevented so a click never pulls
                // focus out of the box in the first place; Space/Enter on a
                // tabbed-to chip still select it.
                // Disabled while a note is being edited: the edit keeps its own
                // outcome, and a chip lighting up then would look like it changed it.
                <button key={c.code} type="button" aria-pressed={on} disabled={editId != null}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { const next = on ? null : c.code; setCode(next); if (next) addRef.current?.focus() }}
                  title={on ? 'กดอีกครั้งเพื่อยกเลิกการเลือก' : c.thai}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11.5px] font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${c.cls} ${on ? 'ring-2 ring-brand-500 ring-offset-1' : ''}`}>
                  {on ? <Check size={11} strokeWidth={3} /> : <OutcomeIcon outcome={c} />}{c.thai}
                </button>
              )
            })}
          </div>
          {/* A key for the groups whose effect outlasts the call (พัก · ยกเลิกเคส):
              the chips by name, then what they do. Text in muted ink; the icon and
              chip colour carry the group. */}
          {OUTCOME_GROUPS.filter((g) => g.key).map((g) => {
            const chips = quickCodes.filter((c) => c.group === g.id)
            if (!chips.length) return null
            return (
              <p key={g.id} className="mt-1.5 flex items-start gap-1 text-[11.5px] text-slate-500">
                <span className={`mt-px inline-flex rounded border p-0.5 ${chips[0].cls}`}><OutcomeIcon outcome={chips[0]} size={10} /></span>
                <span><span className="font-medium text-slate-600">{chips.map((c) => c.thai).join(' · ')}</span> — {g.key}</span>
              </p>
            )
          })}
          {/* A note with no outcome still saves — say it will not be counted,
              rather than refusing it. */}
          {/* NO / OC park the patient until a new round — the full warning BEFORE
              the save, while it is still one tap to change. */}
          {picked?.excludes && (
            <p className="mt-1.5 text-[11.5px] text-amber-700">{EXCLUDE_WARNING}</p>
          )}
          {picked?.cancels && (
            <p className="mt-1.5 text-[11.5px] text-red-700">กดเพิ่มโน้ตแล้วจะถามยืนยันก่อน — ยกเลิกเคสแล้ว ลบโน้ตไม่ได้คืนสถานะ</p>
          )}
          {adding.trim() && !picked && (
            <p className="mt-1.5 text-[11.5px] text-amber-700">
              ยังไม่ได้เลือกผลการโทร — กดเลือกหนึ่งปุ่มด้านบน เพื่อให้รายงานนับผลได้
            </p>
          )}
        </div>
      )}

      {/* add box */}
      {readOnly ? (
        <p className="mb-2 text-[11.5px] text-slate-400">ดูอย่างเดียว — บันทึกผลการโทรในแท็บรอโทร · บันทึกของผู้รีวิวอยู่ในส่วนรีวิวก่อนโทรด้านบน</p>
      ) : (
      <div className="flex items-center gap-2 mb-2.5">
        {/* Esc here clears the draft only — stopped so it does not also close the
            Drawer this section sits in (a draft is not worth a lost drawer). */}
        <Input ref={addRef} value={adding} onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addNote(); if (e.key === 'Escape' && (adding || picked)) { e.stopPropagation(); clearDraft() } }}
          placeholder={!quickCodes.length ? 'บันทึกผลการติดตาม เช่น โทรแล้ว นัด จ. หน้า…'
            : picked ? `${picked.thai} — พิมพ์รายละเอียดเพิ่ม (ถ้ามี)…` : 'เลือกผลการโทรด้านบน แล้วพิมพ์รายละเอียด…'}
          aria-label="โน้ต Recall" className="flex-1" />
        {(adding || picked) && (
          <Button size="sm" variant="secondary" onClick={clearDraft} disabled={busy}>ยกเลิก</Button>
        )}
        <Button size="sm" onClick={addNote} loading={busy && editId == null && !confirmDel} disabled={busy || (!adding.trim() && !picked)}>เพิ่มโน้ต</Button>
      </div>
      )}
      {err && <div className="mb-2"><InlineError>{err}</InlineError></div>}
      {rows == null ? <Loading variant="inline" /> : rows.length === 0 ? <EmptyState variant="compact" title="ยังไม่มีประวัติการติดตาม" /> : (
        <div className="space-y-1.5">
          {rows.map((r) => {
            const authorId = noteAuthorId(r)
            const mine = meId != null && authorId != null && String(authorId) === String(meId)
            const editing = editId === r.id
            const time = fmtTime(r.created_at)
            const code = authorCode(r)
            return (
              <div key={r.id} className="group rounded-lg border border-slate-100 bg-slate-50/50 px-2.5 py-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-0.5">
                  <span className="tabular-nums">{fmtDate(r.date ?? r.created_at)}</span>
                  {time && <span className="tabular-nums">· {time}</span>}
                  {code && <span className="font-medium text-slate-500">· {code}</span>}
                  {r.success === true && <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium"><Check size={11} strokeWidth={3} />สำเร็จ</span>}
                  {!readOnly && <span className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                    <IconButton size="sm" icon={Pencil} label="แก้ไข" disabled={!mine}
                      disabledReason="แก้ไขได้เฉพาะโน้ตที่คุณสร้าง"
                      onClick={() => { setEditId(r.id); setEditText(splitNote(r.Note).text) }} />
                    {/* Admin path matches functions/api/recall/[id].js exactly: it refuses a
                        note with no recorded author (Bubble-era rows) for everyone. */}
                    <IconButton size="sm" icon={Trash2} variant="danger-soft"
                      label={mine ? 'ลบ' : 'ลบ (แอดมิน)'}
                      disabled={!mine && !(canModerate && authorId != null)}
                      disabledReason="ลบได้เฉพาะโน้ตที่คุณสร้าง"
                      onClick={() => setConfirmDel(r)} />
                  </span>}
                </div>
                {editing ? (
                  <div>
                    <div className="flex items-center gap-1.5">
                      {splitNote(r.Note).code && <OutcomePill note={splitNote(r.Note)} />}
                      <Input autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(r); if (e.key === 'Escape') { e.stopPropagation(); setEditId(null) } }}
                        aria-label="แก้ไขโน้ต" className="flex-1 min-w-0 !py-1" />
                      <IconButton size="sm" icon={Check} label="บันทึก" variant="soft" onClick={() => saveEdit(r)} disabled={busy || (!editText.trim() && !splitNote(r.Note).code)} />
                      <IconButton size="sm" icon={X} label="ยกเลิก" onClick={() => setEditId(null)} />
                    </div>
                    {splitNote(r.Note).code && (
                      <p className="mt-1 text-[11px] text-slate-400">แก้ได้เฉพาะข้อความ — ถ้าเลือกผลการโทรผิด ให้ลบโน้ตนี้แล้วเพิ่มใหม่</p>
                    )}
                  </div>
                ) : (
                  <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{r.Note ? <RecallNoteText note={r.Note} /> : <span className="text-slate-300">— (ไม่มีข้อความ)</span>}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
      {/* Portalled to <body>: inside the recall Drawer the panel would otherwise
          confine a fixed overlay to its own width. */}
      {confirmCancel && picked?.cancels && createPortal(
        <ConfirmDialog
          tone="danger"
          title="ยกเลิกเคสคนไข้รายนี้?"
          message={`${callMissing ? '⚠ ยังไม่พบประวัติการโทรของคนไข้รายนี้วันนี้ · ' : ''}สถานะคนไข้จะเปลี่ยนเป็น “${CANCEL_STATUS}” และจะไม่ขึ้นใน recall ทุกประเภทอีก (ทั้ง recall ปกติและรากเทียม) · ถ้ากดผิด ต้องเปิดหน้าเต็มแล้วเปลี่ยนสถานะกลับเอง — การลบโน้ตไม่ได้คืนสถานะ`}
          confirmLabel="ยกเลิกเคส"
          cancelLabel="กลับ"
          busy={busy}
          onConfirm={cancelCase}
          onCancel={() => setConfirmCancel(false)}
        />,
        document.body,
      )}
      {confirmNoCall && picked && createPortal(
        <ConfirmDialog
          title="ยังไม่พบประวัติการโทรวันนี้"
          message={`บันทึกผล “${picked.thai}” โดยไม่มีสายที่โทรหาคนไข้รายนี้วันนี้ ถือว่าผิดปกติ · ถ้าเพิ่งวางสาย ประวัติการโทรมักเข้าภายใน 1 นาที (บางครั้ง 2–3 นาที) — กด “รอก่อน” แล้วบันทึกใหม่ · ถ้าโทรจากเบอร์อื่น หรือเบอร์ในระบบไม่ตรงกับเบอร์ที่โทร ระบบจะหาไม่เจอ`}
          confirmLabel="บันทึกต่อ"
          cancelLabel="รอก่อน"
          busy={busy}
          onConfirm={async () => { await saveNote(draftNote()); setConfirmNoCall(false) }}
          onCancel={() => setConfirmNoCall(false)}
        />,
        document.body,
      )}
      {confirmDel && createPortal(
        <ConfirmDialog
          tone="danger"
          title="ลบโน้ต Recall นี้?"
          message={`โน้ตวันที่ ${fmtDate(confirmDel.date ?? confirmDel.created_at)}${splitNote(confirmDel.Note).text ? ` “${splitNote(confirmDel.Note).text}”` : ''} จะถูกลบถาวร`}
          confirmLabel="ลบโน้ต"
          busy={busy}
          onConfirm={() => del(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />,
        document.body,
      )}
    </Section>
  )
}
export const recallTs = (r) => {
  const v = r?.created_at ?? r?.date
  if (typeof v === 'number') return v
  const s = String(v || '')
  if (/^\d{10,}$/.test(s)) return Number(s)
  return new Date(s).getTime() || 0
}
// `created_by` is the column the POST stack writes; `creator` is legacy. Either can
// be a bare id or an expanded object — normalise to the numeric id.
export const noteAuthorId = (r) => r?.created_by?.id ?? r?.created_by ?? r?.creator?.id ?? r?.creator ?? null
